import { useState, useRef, useEffect } from "react";
import { Bot, X, Send } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { API } from "@/lib/api";

const WELCOME = {
  role: "assistant",
  content: "Namaste! 🙏 I'm your VYAPAAR assistant. Tell me what you're looking for — city, budget, BHK, buy or rent — and I'll find the perfect match.",
};

export default function AIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([WELCOME]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const convId = useRef(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async () => {
    const text = input.trim();
    if (!text || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setLoading(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ conversation_id: convId.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let firstChunk = true;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        let chunk = decoder.decode(value, { stream: true });
        if (firstChunk && chunk.startsWith("__CONVERSATION_ID__:")) {
          const nl = chunk.indexOf("\n");
          convId.current = chunk.substring("__CONVERSATION_ID__:".length, nl).trim();
          chunk = chunk.substring(nl + 1);
          firstChunk = false;
        }
        acc += chunk;
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: acc };
          return copy;
        });
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: "Sorry, I hit a snag. Please try again." };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        data-testid="ai-chat-fab"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-24 right-6 z-50 h-14 w-14 rounded-full bg-gold text-navy shadow-xl flex items-center justify-center hover:scale-105 transition-transform"
      >
        {open ? <X className="h-6 w-6" /> : <Bot className="h-6 w-6" />}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            data-testid="ai-chat-panel"
            className="fixed bottom-44 right-6 z-50 w-[calc(100vw-3rem)] sm:w-96 h-[520px] bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden"
          >
            <div className="bg-navy text-white px-4 py-3 flex items-center gap-3">
              <div className="h-9 w-9 rounded-full bg-gold flex items-center justify-center">
                <Bot className="h-5 w-5" />
              </div>
              <div>
                <p className="font-display font-semibold leading-tight">VYAPAAR Assistant</p>
                <p className="text-xs text-blue-100">AI property finder</p>
              </div>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 no-scrollbar bg-slate-50">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-navy text-white rounded-br-sm"
                        : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"
                    }`}
                  >
                    {m.content || (loading && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
            </div>

            <div className="p-3 border-t border-slate-200 flex items-center gap-2">
              <input
                data-testid="ai-chat-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="e.g. 2BHK in Pune under 50 lakh"
                className="flex-1 px-3.5 py-2.5 rounded-full border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-navy"
              />
              <button
                data-testid="ai-chat-send"
                onClick={send}
                disabled={loading}
                className="h-10 w-10 rounded-full bg-gold text-navy flex items-center justify-center hover:bg-gold-dark disabled:opacity-50 transition-colors"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
